"use client";

import React, { useState, useEffect, useCallback } from "react";

import BasicInputs from "./BasicInputs";
import AdvancedInputs from "./AdvancedInputs";
import MortgageResult from "./MortgageResult";

/* ────────────────────────────────────────────────────────────────── */

const toCurrency = (n: number) =>
    !isFinite(n) || isNaN(n) ? "$0.00" : "$" + n.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");

/* very coarse PMI rule of thumb                        */
const monthlyPMI = (loanAmt: number, ltv: number) => (ltv > 80 ? (0.0075 * loanAmt) / 12 : 0);

/* ────────────────────────────────────────────────────────────────── */

const PriceRange = () => {
    /* BASIC section */
    const [downPayment, setDownPayment] = useState<number>(0);
    const [totalMonthly, setTotalMonthly] = useState<number>(0);
    const [loanTerm, setLoanTerm] = useState<number>(0);
    const [mortgageRate, setMortgageRate] = useState<number>(0);

    /* ADVANCED section */
    const [ltv, setLtv] = useState<number>(1);
    const [propertyTax, setPropertyTax] = useState<number>(0); // % of home value
    const [insurance, setInsurance] = useState<number>(0);

    /* derived results for the <results> accordion */
    const [calc, setCalc] = useState<{
        homePrice: number;
        loanAmount: number;
        principalAndInterest: number;
        taxesAndInsurance: number;
        pmi: number;
        total: number;
    } | null>(null);

    const [resultOpen, setResultOpen] = useState(false);

    /* ── core calculator ─────────────────────────────────────────── */
    const handleCalculate = useCallback(() => {
        /* 1. figure out the home price & loan amount from DP + LTV      */
        const homePrice = ltv >= 100 ? 0 : downPayment / (1 - ltv / 100);
        const loanAmount = homePrice - downPayment;

        /* 2. amortised P&I                                              */
        const n = loanTerm * 12;
        const r = mortgageRate / 100 / 12;
        const P = r === 0 ? loanAmount / n : (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

        /* 3. taxes + insurance                                          */
        const monthlyTax = ((propertyTax / 100) * homePrice) / 12;
        const monthlyInsurance = insurance / 12;
        const taxesAndIns = monthlyTax + monthlyInsurance;

        /* 4. PMI (simplified)                                           */
        const pmiMonthly = monthlyPMI(loanAmount, ltv);

        /* 5. grand total                                                */
        const total = P + taxesAndIns + pmiMonthly;

        setCalc({
            homePrice,
            loanAmount,
            principalAndInterest: P,
            taxesAndInsurance: taxesAndIns,
            pmi: pmiMonthly,
            total,
        });
    }, [downPayment, ltv, loanTerm, mortgageRate, propertyTax, insurance]);

    useEffect(handleCalculate, [handleCalculate]);

    useEffect(() => {
        if (mortgageRate > 0) {
            setResultOpen(true);
        }
    }, [mortgageRate]);

    /* ─────────────────────────────────────────────────────────────── */

    return (
        <main id="main-content" tabIndex={-1} className="2xl:py-10 2xl:px-4 2xl:pb-60" aria-labelledby="page-title">
            <div className="max-w-3xl mx-auto 2xl:pl-[28px]">
                {/* page heading */}
                <div className="text-center mb-4 2xl:w-[900px]">
                    <h1 id="page-title" className="text-2xl md:text-3xl font-bold uppercase text-[#212459] mb-2">
                        Max Mortgage Payment Calculator
                    </h1>
                </div>

                <div className="pb-[30px] w-[350px] mx-auto 2xl:w-[1200px] 2xl:mx-0">
                    {/* BASIC */}
                    <BasicInputs
                        downPayment={downPayment}
                        setDownPayment={setDownPayment}
                        totalMonthly={totalMonthly}
                        setTotalMonthly={setTotalMonthly}
                        loanTerm={loanTerm}
                        setLoanTerm={setLoanTerm}
                        mortgageRate={mortgageRate}
                        setMortgageRate={setMortgageRate}
                    />

                    {/* ADVANCED */}
                    <AdvancedInputs
                        ltv={ltv}
                        setLtv={setLtv}
                        propertyTax={propertyTax}
                        setPropertyTax={setPropertyTax}
                        insurance={insurance}
                        setInsurance={setInsurance}
                    />

                    {/* RESULTS */}
                    {calc && (
                        <MortgageResult
                            /* headline */
                            homePrice={calc.homePrice}
                            /* first‑row breakdown */
                            loanAmount={calc.loanAmount}
                            downPayment={downPayment}
                            totalMonthly={calc.total}
                            /* second‑row breakdown */
                            principalAndInterest={calc.principalAndInterest}
                            pmi={calc.pmi}
                            taxesAndInsurance={calc.taxesAndInsurance}
                            /* charts */
                            annualRate={mortgageRate}
                            loanTerm={loanTerm}
                            /* utils */
                            toCurrency={toCurrency}
                            isOpen={resultOpen}
                            setIsOpen={setResultOpen}
                        />
                    )}
                </div>
            </div>
        </main>
    );
};

export default PriceRange;
